import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { QuickReplyDTO } from '@sac/contracts'
import { api } from '../../lib/api'
import { qk } from '../../lib/query'
import { useToast } from '../ui/Toaster'

/**
 * Configurações → Respostas rápidas: os textos do raio no compositor. Qualquer atendente cria,
 * edita e apaga (decisão do Daniel, 28/10).
 */

type Draft = { id?: string; title: string; body: string }

export function QuickRepliesTab() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data = [], isLoading } = useQuery({ queryKey: qk.quickReplies(), queryFn: api.quickReplies })
  const [edit, setEdit] = useState<Draft | null>(null)

  const done = (msg: string) => {
    toast(msg)
    setEdit(null)
    void qc.invalidateQueries({ queryKey: qk.quickReplies() })
  }
  const save = useMutation({
    mutationFn: api.saveQuickReply,
    onSuccess: () => done('Resposta rápida salva.'),
    onError: () => toast('Não consegui salvar. Título até 80 caracteres e texto até 4000.'),
  })
  const del = useMutation({
    mutationFn: api.deleteQuickReply,
    onSuccess: () => done('Resposta rápida apagada.'),
    onError: () => toast('Não consegui apagar.'),
  })

  const form = (d: Draft) => (
    <div className="cfg-block">
      <div className="field">
        <label>Título</label>
        <input autoFocus maxLength={80} value={d.title} onChange={(e) => setEdit({ ...d, title: e.target.value })} />
      </div>
      <div className="field">
        <label>Texto</label>
        <textarea rows={6} maxLength={4000} value={d.body} onChange={(e) => setEdit({ ...d, body: e.target.value })} />
      </div>
      <div className="cfg-actions">
        <button className="btn pri sm" disabled={save.isPending || !d.title.trim() || !d.body.trim()} onClick={() => save.mutate(d)}>
          Salvar
        </button>
        <button className="btn ghost sm" onClick={() => setEdit(null)}>
          Cancelar
        </button>
      </div>
    </div>
  )

  return (
    <>
      <div className="crm-head">
        <h2>Respostas rápidas</h2>
        <p>Os textos que aparecem no raio ⚡ da caixa de resposta. Qualquer pessoa da equipe pode criar, editar e apagar.</p>
      </div>

      {edit && !edit.id ? (
        form(edit)
      ) : (
        <div style={{ marginBottom: 12 }}>
          <button className="btn pri sm" onClick={() => setEdit({ title: '', body: '' })}>
            Nova resposta rápida
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="empty">Carregando…</div>
      ) : data.length === 0 ? (
        <div className="empty">Nenhuma resposta rápida ainda.</div>
      ) : (
        <div className="cfg-block">
          {data.map((q: QuickReplyDTO) =>
            edit?.id === q.id ? (
              <div key={q.id}>{form(edit)}</div>
            ) : (
              <div className="cfg-row" key={q.id}>
                <div className="cfg-main">
                  <div className="cfg-title">{q.title}</div>
                  <div className="cfg-note">{q.body}</div>
                </div>
                <div className="cfg-actions">
                  <button className="btn ghost sm" onClick={() => setEdit(q)}>
                    Editar
                  </button>
                  <button
                    className="btn ghost sm"
                    disabled={del.isPending}
                    onClick={() => window.confirm(`Apagar "${q.title}"?`) && del.mutate(q.id)}
                  >
                    Apagar
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </>
  )
}
